import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, BarChart3 } from "lucide-react";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedSector, setSelectedSector] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [citiesRes, sectorsRes] = await Promise.all([
          axios.get(`${API}/cities`),
          axios.get(`${API}/sectors`),
        ]);
        setCities(citiesRes.data);
        // Filter out "All Sectors" entry for the selector
        const realSectors = sectorsRes.data.filter(s => s.key !== "all");
        setSectors(realSectors);
        if (citiesRes.data.length > 0) setSelectedCity(citiesRes.data[0].key);
        if (realSectors.length > 0) setSelectedSector(realSectors[0].key);
      } catch (err) {
        console.error("Failed to load data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleFindPatterns = () => {
    if (!selectedCity || !selectedSector) return;
    navigate(`/pattern-match?city=${selectedCity}&sector=${selectedSector}`);
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-8"
      style={{ background: "radial-gradient(ellipse at 50% 40%, #0f172a 0%, #09090B 70%)" }}
      data-testid="dashboard-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg text-center"
      >
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="/logo.png"
            alt="Stock Correlations"
            className="h-24 w-auto"
          />
        </div>

        {/* Heading */}
        <h1 className="font-heading text-4xl font-black text-white mb-3">
          Australian Weather &<br />Stock Analysis
        </h1>
        <p className="text-[#A1A1AA] text-base mb-10">
          Select a capital city to explore how weather patterns<br />might correlate with stocks.
        </p>

        {/* Selectors + Button card */}
        <div className="bg-[#18181B] border border-[#27272A] rounded-xl p-6 space-y-4 text-left">

          {/* City selector */}
          <div>
            <label className="flex items-center gap-2 text-xs font-data uppercase tracking-wider text-[#A1A1AA] mb-2">
              <MapPin className="w-3.5 h-3.5" /> Capital City
            </label>
            <Select value={selectedCity} onValueChange={setSelectedCity} disabled={loading}>
              <SelectTrigger className="bg-[#09090B] border-[#27272A] text-white w-full" data-testid="city-select">
                <SelectValue placeholder={loading ? "Loading..." : "Select a city"} />
              </SelectTrigger>
              <SelectContent className="bg-[#18181B] border-[#27272A]">
                {cities.map(city => (
                  <SelectItem key={city.key} value={city.key} className="text-white hover:bg-[#27272A]">
                    {city.name}, {city.state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sector selector */}
          <div>
            <label className="flex items-center gap-2 text-xs font-data uppercase tracking-wider text-[#A1A1AA] mb-2">
              <BarChart3 className="w-3.5 h-3.5" /> Stock Sector
            </label>
            <Select value={selectedSector} onValueChange={setSelectedSector} disabled={loading}>
              <SelectTrigger className="bg-[#09090B] border-[#27272A] text-white w-full" data-testid="sector-select">
                <SelectValue placeholder={loading ? "Loading..." : "Select a sector"} />
              </SelectTrigger>
              <SelectContent className="bg-[#18181B] border-[#27272A]">
                {sectors.map(sector => (
                  <SelectItem key={sector.key} value={sector.key} className="text-white hover:bg-[#27272A]">
                    {sector.name} ({sector.stock_count} stocks)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Find patterns button */}
          <Button
            onClick={handleFindPatterns}
            disabled={loading || !selectedCity || !selectedSector}
            className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-black font-bold text-base h-12 mt-2"
            data-testid="find-patterns-button"
          >
            <Search className="w-4 h-4 mr-2" />
            Find Matching Patterns
          </Button>
        </div>

        {/* Footer note */}
        <p className="text-[#52525B] text-xs mt-6">
          Data sourced from Open-Meteo &amp; Yahoo Finance · ASX listed securities only
        </p>
      </motion.div>
    </div>
  );
}
